import React, { useEffect, useMemo, useRef, useState } from "react";

import InfoOutlined from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import { LinearProgress } from "@mui/material";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Button } from "components";

import { snackbarData, userCredentials } from "recoilState/GlobalState";
import {
    MIAConnectionIns,
    MIAConnectionUpd,
} from "services/services-mia";

import CSVFieldsInfoModal from "./CSVFieldsInfoModal";
import styles from "styles/pages/ModalActions.module.scss";

const EXPECTED_HEADERS = [
    "rolCod",
    "tipoDeDocumentoCod",
    "usuarioNroDeDocumentoX",
    "usuarioSexo",
    "usuarioFechaDeNacimiento",
    "usuarioMail",
    "usuarioCelular",
    "usuarioApellidos",
    "usuarioNombres",
    "merchantServicioUsuarioForzado",
    "merchantServicioUsuarioStatus",
    "Operacion",
    "resultado",
    "errmsg",
    "timestamp",
];

const REQUIRED_HEADERS = EXPECTED_HEADERS.slice(0, 12);
const OPERATIONS = ["A", "M", "B"];
const SEX_OPTIONS = ["F", "M", "X"];
const FORCE_OPTIONS = ["Y", "N"];

const normalizeValue = (value) =>
    value == null ? "" : String(value).replace(/^\uFEFF/, "").trim();

const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (char === "," && !inQuotes) {
            result.push(current);
            current = "";
            continue;
        }

        current += char;
    }

    result.push(current);
    return result;
};

const escapeCSVValue = (value) => {
    const text = value == null ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
};

const parseCSVText = (text) => {
    const lines = text
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .filter((line) => line.trim() !== "");

    if (lines.length === 0) {
        return { headers: [], rows: [] };
    }

    const headers = parseCSVLine(lines[0]).map(normalizeValue);
    const rows = lines.slice(1).map((line, index) => {
        const values = parseCSVLine(line);
        const row = {};

        headers.forEach((header, headerIndex) => {
            row[header] = normalizeValue(values[headerIndex] || "");
        });

        return {
            rowNumber: index + 2,
            ...row,
        };
    });

    return { headers, rows };
};

const isValidDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

    const date = new Date(`${value}T00:00:00`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

const downloadTextFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const downloadResultCSV = (rows, serviceName) => {
    const dt = new Date();
    const dateString = `${dt.getFullYear()}${`${dt.getMonth() + 1}`.padStart(2, "0")}${`${dt.getDate()}`.padStart(2, "0")}${`${dt.getHours()}`.padStart(2, "0")}${`${dt.getMinutes()}`.padStart(2, "0")}${`${dt.getSeconds()}`.padStart(2, "0")}`;
    const fileName = `BatchConexiones-${serviceName || "servicio"}-${dateString}.csv`;
    const content = [
        EXPECTED_HEADERS.join(","),
        ...rows.map((row) =>
            EXPECTED_HEADERS.map((header) => escapeCSVValue(row[header] || "")).join(",")
        ),
    ].join("\n");

    downloadTextFile(content, fileName, "text/csv;charset=utf-8;");
};

const normalizeRow = (row) => {
    const normalized = {};

    EXPECTED_HEADERS.forEach((header) => {
        normalized[header] = normalizeValue(row[header]);
    });

    normalized.Operacion = normalized.Operacion.toUpperCase();
    normalized.rolCod = normalized.rolCod.toUpperCase();
    normalized.tipoDeDocumentoCod = normalized.tipoDeDocumentoCod.toUpperCase();
    normalized.usuarioSexo = normalized.usuarioSexo.toUpperCase();
    normalized.merchantServicioUsuarioForzado =
        normalized.merchantServicioUsuarioForzado.toUpperCase();
    normalized.merchantServicioUsuarioStatus =
        normalized.merchantServicioUsuarioStatus.toUpperCase();

    if (normalized.Operacion === "B") {
        normalized.merchantServicioUsuarioStatus = "B";
    }

    return normalized;
};

const validateRow = (row, allowedRoles) => {
    const errors = [];

    if (!OPERATIONS.includes(row.Operacion)) {
        errors.push("Operacion invalida");
    }
    if (!row.rolCod) {
        errors.push("rolCod requerido");
    } else if (allowedRoles.length > 0 && !allowedRoles.includes(row.rolCod)) {
        errors.push("rolCod invalido");
    }
    if (!row.tipoDeDocumentoCod) {
        errors.push("tipoDeDocumentoCod requerido");
    }
    if (!row.usuarioNroDeDocumentoX) {
        errors.push("usuarioNroDeDocumentoX requerido");
    }
    if (!row.usuarioSexo || !SEX_OPTIONS.includes(row.usuarioSexo)) {
        errors.push("usuarioSexo invalido");
    }
    if (!row.usuarioFechaDeNacimiento || !isValidDate(row.usuarioFechaDeNacimiento)) {
        errors.push("usuarioFechaDeNacimiento invalida");
    }
    if (
        !row.merchantServicioUsuarioForzado ||
        !FORCE_OPTIONS.includes(row.merchantServicioUsuarioForzado)
    ) {
        errors.push("merchantServicioUsuarioForzado invalido");
    }
    if (row.Operacion === "M" && !row.merchantServicioUsuarioStatus) {
        errors.push("merchantServicioUsuarioStatus requerido para modificacion");
    }

    return errors;
};

const getInitialSummary = () => ({
    total: 0,
    valid: 0,
    localErrors: 0,
    processed: 0,
    ok: 0,
    error: 0,
});

export default function BatchModal({
    isOpen,
    servicio,
    roles,
    updateList,
    closeModal,
}) {
    const credentials = useRecoilValue(userCredentials);
    const setSnackbarInfo = useSetRecoilState(snackbarData);

    const fileInputRef = useRef(null);
    const stopRequestedRef = useRef(false);

    const [fileName, setFileName] = useState("");
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(getInitialSummary());
    const [missingHeaders, setMissingHeaders] = useState([]);
    const [parseError, setParseError] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [fieldsModalOpen, setFieldsModalOpen] = useState(false);
    const [inRequest, setInRequest] = useState(false);
    const [stopRequested, setStopRequested] = useState(false);
    const [finished, setFinished] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [downloadRows, setDownloadRows] = useState([]);

    const allowedRoles = useMemo(
        () => (roles || []).map((role) => role.rolCod).filter(Boolean),
        [roles]
    );

    const canStart =
        rows.length > 0 &&
        missingHeaders.length === 0 &&
        !parseError &&
        !inRequest;

    const progressValue = summary.total
        ? Math.round((summary.processed * 100) / summary.total)
        : 0;

    const showSnackbar = (message, severity = "info") => {
        setSnackbarInfo({
            message,
            severity,
            open: true,
        });
    };

    const resetState = () => {
        stopRequestedRef.current = false;
        setFileName("");
        setRows([]);
        setSummary(getInitialSummary());
        setMissingHeaders([]);
        setParseError("");
        setInRequest(false);
        setStopRequested(false);
        setFinished(false);
        setHasStarted(false);
        setDownloadRows([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        if (isOpen) {
            resetState();
        }
    }, [isOpen]);

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];

        stopRequestedRef.current = false;
        setStopRequested(false);
        setFinished(false);
        setHasStarted(false);
        setDownloadRows([]);
        setRows([]);
        setSummary(getInitialSummary());
        setMissingHeaders([]);
        setParseError("");

        if (!file) {
            setFileName("");
            return;
        }

        setFileName(file.name);

        try {
            const text = await file.text();
            const { headers, rows: parsedRows } = parseCSVText(text);
            const missing = REQUIRED_HEADERS.filter(
                (header) => !headers.includes(header)
            );

            setMissingHeaders(missing);

            if (missing.length > 0) {
                setParseError(
                    `Faltan columnas requeridas: ${missing.join(", ")}`
                );
                return;
            }

            const hydratedRows = parsedRows.map((row) => {
                const normalized = normalizeRow(row);
                const validationErrors = validateRow(normalized, allowedRoles);

                return {
                    ...normalized,
                    resultado: validationErrors.length > 0 ? "ERROR" : "",
                    errmsg: validationErrors.join(" | "),
                    _rowNumber: row.rowNumber,
                    _validationErrors: validationErrors,
                };
            });

            setRows(hydratedRows);
            setSummary({
                total: hydratedRows.length,
                valid: hydratedRows.filter((row) => row._validationErrors.length === 0)
                    .length,
                localErrors: hydratedRows.filter(
                    (row) => row._validationErrors.length > 0
                ).length,
                processed: 0,
                ok: 0,
                error: 0,
            });
        } catch (error) {
            setParseError("No se pudo leer el archivo CSV");
        }
    };

    const requestForRow = async (row) => {
        const payload = {
            servicioCod: servicio.servicioCod,
            rolCod: row.rolCod,
            tipoDeDocumentoCod: row.tipoDeDocumentoCod,
            usuarioNroDeDocumentoX: row.usuarioNroDeDocumentoX,
            usuarioSexo: row.usuarioSexo,
            usuarioFechaDeNacimiento: row.usuarioFechaDeNacimiento,
            usuarioMail: row.usuarioMail,
            usuarioCelular: row.usuarioCelular,
            usuarioApellidos: row.usuarioApellidos,
            usuarioNombres: row.usuarioNombres,
            merchantServicioUsuarioForzado: row.merchantServicioUsuarioForzado,
            merchantServicioUsuarioStatus: row.merchantServicioUsuarioStatus,
        };
        if (row.Operacion === "A") {
            return await MIAConnectionIns(payload, credentials);
        }

        return await MIAConnectionUpd(payload, credentials);
    };

    const handleStop = () => {
        stopRequestedRef.current = true;
        setStopRequested(true);
    };

    const handleProcess = async () => {
        if (!servicio?.servicioCod) {
            showSnackbar("No hay servicio seleccionado", "error");
            return;
        }

        setInRequest(true);
        setFinished(false);
        setHasStarted(true);
        stopRequestedRef.current = false;
        setStopRequested(false);

        let processed = 0;
        let ok = 0;
        let error = 0;
        const outputRows = [...rows];

        for (let index = 0; index < outputRows.length; index++) {
            if (stopRequestedRef.current) {
                break;
            }

            const row = outputRows[index];

            if (row._validationErrors.length > 0) {
                error++;
                processed++;
                outputRows[index] = {
                    ...row,
                    resultado: "ERROR",
                    errmsg: row.errmsg || "Fila con errores de validacion",
                    timestamp: new Date().toISOString(),
                };
                setRows([...outputRows]);
                setSummary((current) => ({
                    ...current,
                    processed,
                    ok,
                    error,
                }));
                continue;
            }

            const response = await requestForRow(row);
            const success = response?.status?.code === 1;

            outputRows[index] = {
                ...row,
                resultado: success ? "OK" : "ERROR",
                errmsg: success ? "" : response?.status?.errmsg || "Error desconocido",
                timestamp: new Date().toISOString(),
            };

            if (success) {
                ok++;
            } else {
                error++;
            }
            processed++;

            setRows([...outputRows]);
            setSummary((current) => ({
                ...current,
                processed,
                ok,
                error,
            }));
        }

        setInRequest(false);
        setFinished(true);
        setDownloadRows(outputRows);
        downloadResultCSV(outputRows, servicio?.servicioNombre);
        updateList();

        if (stopRequestedRef.current) {
            showSnackbar("Proceso detenido. Se descargo el CSV parcial.", "warning");
            return;
        }

        showSnackbar("Proceso finalizado. Se descargo el CSV resultado.", "success");
    };

    return (
        <>
        <div className={styles.inputs}>
            <div className={`${styles["column-sm"]} ${styles["batch-container"]}`}>
                <div
                    className={`${styles["drop-zone"]} ${isDragging ? styles["drop-zone-active"] : ""}`}
                    onDragOver={(e) => { e.preventDefault(); if (!inRequest) setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        if (inRequest) return;
                        const file = e.dataTransfer.files[0];
                        if (file) handleFileChange({ target: { files: [file] } });
                    }}
                    onClick={() => !inRequest && fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleFileChange}
                        disabled={inRequest}
                        style={{ display: "none" }}
                    />
                    <p className={styles["drop-zone-text"]}>
                        Arrastre un archivo CSV aquí o haga clic para seleccionar
                    </p>
                    <InsertDriveFileOutlined className={styles["drop-zone-icon"]} />
                    {fileName && (
                        <span className={styles["file-name"]}>{fileName}</span>
                    )}
                </div>

                <p className={styles["info-text"]}>
                    <InfoOutlined fontSize="small" />
                    <span
                        className={styles["template-link"]}
                        onClick={() => setFieldsModalOpen(true)}
                    >
                        Ver detalle de los campos del CSV
                    </span>
                </p>

                <div className={styles["actions-row"]}>
                    <Button className={styles.defaultBtn} onClick={closeModal} disabled={inRequest}>
                        Cerrar
                    </Button>
                    {!hasStarted && (
                        <Button className={styles.defaultBtn} onClick={handleProcess} disabled={!canStart}>
                            Iniciar Procesamiento
                        </Button>
                    )}
                    {inRequest && (
                        <Button className={styles.defaultBtn} onClick={handleStop}>Parar</Button>
                    )}
                    {hasStarted &&
                        !inRequest &&
                        finished &&
                        downloadRows.length > 0 && (
                            <Button
                                className={styles.defaultBtn}
                                onClick={() => downloadResultCSV(downloadRows, servicio?.servicioNombre)}
                            >
                                Descargar resultado
                            </Button>
                        )}
                </div>

                {parseError && missingHeaders.length === 0 && (
                    <div className={styles["error-text"]}>{parseError}</div>
                )}

                {missingHeaders.length > 0 && (
                    <div className={styles["error-text"]}>
                        Faltan columnas: {missingHeaders.join(", ")}
                    </div>
                )}

                {hasStarted && summary.total > 0 && (
                    <div className={styles["progress-wrap"]}>
                        <LinearProgress
                            variant="determinate"
                            value={progressValue}
                            color="secondary"
                        />
                    </div>
                )}

                {summary.total > 0 && (
                    <div className={styles["status-wrap"]}>
                        <div>Procesadas: {summary.processed} / {summary.total}</div>
                        {stopRequested && <div>Se detendra antes de la siguiente fila.</div>}
                        {finished && !stopRequested && <div>Proceso completo.</div>}
                    </div>
                )}
            </div>
        </div>

        <CSVFieldsInfoModal
            open={fieldsModalOpen}
            onClose={() => setFieldsModalOpen(false)}
        />
        </>
    );
}
